import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as RunIcon,
  Schedule as ScheduleIcon,
  FileCopy as CloneIcon,
  History as HistoryIcon,
  Assessment as ReportIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import axios from 'axios';

interface ScheduledReport {
  _id: string;
  reportId: string;
  name: string;
  description: string;
  reportType: string;
  schedule: {
    frequency: string;
    timeOfDay: string;
    timezone: string;
    dayOfWeek?: number;
    dayOfMonth?: number;
    customCron?: string;
  };
  parameters: {
    dateRange: {
      type: string;
    };
    format: string;
    includeCharts: boolean;
    locations: string[];
    includeAllLocations: boolean;
  };
  delivery: {
    email: {
      enabled: boolean;
      recipients: Array<{ email: string; name: string; }>;
    };
  };
  options: {
    enabled: boolean;
  };
  status: string;
  execution: {
    nextRun: string;
    lastRun: string;
    lastSuccess: string;
    runCount: number;
    successCount: number;
    failureCount: number;
  };
  executionStats: {
    successRate: number;
    totalRuns: number;
  };
  access: {
    owner: {
      firstName: string;
      lastName: string;
    };
  };
}

interface ReportTemplate {
  type: string;
  name: string;
  description: string;
  category: string;
  sections: string[];
  formats: string[];
}

const ScheduledReportsManagement: React.FC = () => {
  const [reports, setReports] = useState<ScheduledReport[]>([]);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ScheduledReport | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [reportHistory, setReportHistory] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    reportType: '',
    frequency: 'daily',
    timeOfDay: '09:00',
    timezone: 'America/New_York',
    dayOfWeek: 1,
    dayOfMonth: 1,
    dateRangeType: 'last_day',
    format: 'pdf',
    includeCharts: true,
    includeAllLocations: true,
    emailEnabled: true,
    emailRecipients: [{ email: '', name: '' }],
    enabled: true
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchReports();
    fetchTemplates();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get('/api/scheduled-reports');
      setReports(response.data.data.reports);
    } catch (error) {
      console.error('Error fetching scheduled reports:', error);
      setError('Failed to fetch scheduled reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/scheduled-reports/templates/list');
      setTemplates(response.data.data);
    } catch (error) {
      console.error('Error fetching report templates:', error);
    }
  };

  const handleOpenDialog = (report?: ScheduledReport) => {
    if (report) {
      setSelectedReport(report);
      setFormData({
        name: report.name,
        description: report.description,
        reportType: report.reportType,
        frequency: report.schedule.frequency,
        timeOfDay: report.schedule.timeOfDay,
        timezone: report.schedule.timezone,
        dayOfWeek: report.schedule.dayOfWeek || 1,
        dayOfMonth: report.schedule.dayOfMonth || 1,
        dateRangeType: report.parameters.dateRange.type,
        format: report.parameters.format,
        includeCharts: report.parameters.includeCharts,
        includeAllLocations: report.parameters.includeAllLocations,
        emailEnabled: report.delivery.email.enabled,
        emailRecipients: report.delivery.email.recipients.length > 0 
          ? report.delivery.email.recipients 
          : [{ email: '', name: '' }],
        enabled: report.options.enabled
      });
    } else {
      setSelectedReport(null);
      setFormData({
        name: '',
        description: '',
        reportType: '',
        frequency: 'daily',
        timeOfDay: '09:00',
        timezone: 'America/New_York',
        dayOfWeek: 1,
        dayOfMonth: 1,
        dateRangeType: 'last_day',
        format: 'pdf',
        includeCharts: true,
        includeAllLocations: true,
        emailEnabled: true,
        emailRecipients: [{ email: '', name: '' }],
        enabled: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedReport(null);
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      const reportData = {
        name: formData.name,
        description: formData.description,
        reportType: formData.reportType,
        schedule: {
          frequency: formData.frequency,
          timeOfDay: formData.timeOfDay,
          timezone: formData.timezone,
          dayOfWeek: formData.dayOfWeek,
          dayOfMonth: formData.dayOfMonth
        },
        parameters: {
          dateRange: {
            type: formData.dateRangeType
          },
          format: formData.format,
          includeCharts: formData.includeCharts,
          includeAllLocations: formData.includeAllLocations,
          locations: [],
          sections: templates.find(t => t.type === formData.reportType)?.sections || []
        },
        delivery: {
          email: {
            enabled: formData.emailEnabled,
            recipients: formData.emailRecipients.filter(r => r.email.trim() !== ''),
            subject: `${formData.name} - {{date}}`,
            bodyTemplate: `Please find attached the automated report: ${formData.name} for {{dateRange}}.`,
            includeAttachment: true,
            includeInlinePreview: true
          },
          sms: {
            enabled: false,
            recipients: []
          },
          portal: {
            enabled: true,
            notifyUsers: []
          }
        },
        alerts: {
          enabled: false,
          conditions: []
        },
        options: {
          enabled: formData.enabled,
          retryOnFailure: true,
          maxRetries: 3,
          retryDelay: 300000,
          deleteAfterDays: 90,
          notifyOnFailure: true,
          priority: 'medium'
        }
      };

      if (selectedReport) {
        await axios.put(`/api/scheduled-reports/${selectedReport._id}`, reportData);
        setSuccess('Report updated successfully');
      } else {
        await axios.post('/api/scheduled-reports', reportData);
        setSuccess('Report created successfully');
      }

      fetchReports();
      handleCloseDialog();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to save report');
    }
  };

  const handleDelete = async (reportId: string) => {
    if (window.confirm('Are you sure you want to delete this scheduled report?')) {
      try {
        await axios.delete(`/api/scheduled-reports/${reportId}`);
        setSuccess('Report deleted successfully');
        fetchReports();
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to delete report');
      }
    }
  };

  const handleRun = async (reportId: string) => {
    try {
      setLoading(true);
      await axios.post(`/api/scheduled-reports/${reportId}/run`);
      setSuccess('Report executed successfully');
      fetchReports();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to run report');
    } finally {
      setLoading(false);
    }
  };

  const handleClone = async (report: ScheduledReport) => {
    const newName = prompt('Enter name for cloned report:', `${report.name} (Copy)`);
    if (newName) {
      try {
        await axios.post(`/api/scheduled-reports/${report._id}/clone`, { name: newName });
        setSuccess('Report cloned successfully');
        fetchReports();
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to clone report');
      }
    }
  };

  const handleToggleStatus = async (reportId: string, enabled: boolean) => {
    try {
      await axios.put(`/api/scheduled-reports/${reportId}/status`, { enabled });
      setSuccess(`Report ${enabled ? 'enabled' : 'disabled'} successfully`);
      fetchReports();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update report status');
    }
  };

  const handleViewHistory = async (reportId: string) => {
    try {
      const response = await axios.get(`/api/scheduled-reports/${reportId}/history`);
      setReportHistory(response.data.data.history);
      setHistoryDialogOpen(true);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch report history');
    }
  };

  const addEmailRecipient = () => {
    setFormData({
      ...formData,
      emailRecipients: [...formData.emailRecipients, { email: '', name: '' }]
    });
  };

  const removeEmailRecipient = (index: number) => {
    const newRecipients = formData.emailRecipients.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      emailRecipients: newRecipients.length > 0 ? newRecipients : [{ email: '', name: '' }]
    });
  };

  const updateEmailRecipient = (index: number, field: string, value: string) => {
    const newRecipients = [...formData.emailRecipients];
    newRecipients[index] = { ...newRecipients[index], [field]: value };
    setFormData({
      ...formData,
      emailRecipients: newRecipients
    });
  };

  const getStatusColor = (status: string, enabled: boolean) => {
    if (!enabled) return 'default';
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'disabled': return 'error';
      default: return 'default';
    }
  };

  const formatNextRun = (nextRun: string) => {
    if (!nextRun) return 'Not scheduled';
    const date = new Date(nextRun);
    return date.toLocaleString();
  };

  if (loading && reports.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Scheduled Reports
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create Report
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Reports Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Reports
              </Typography>
              <Typography variant="h4">
                {reports.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Reports
              </Typography>
              <Typography variant="h4">
                {reports.filter(r => r.status === 'active' && r.options.enabled).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Success Rate
              </Typography>
              <Typography variant="h4">
                {reports.length > 0 
                  ? Math.round(reports.reduce((sum, r) => sum + (r.executionStats?.successRate || 0), 0) / reports.length)
                  : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Executions
              </Typography>
              <Typography variant="h4">
                {reports.reduce((sum, r) => sum + (r.execution?.runCount || 0), 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Reports Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Report Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell>Next Run</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Success Rate</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report._id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">{report.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {report.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={report.reportType.replace('_', ' ')} 
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {report.schedule.frequency} at {report.schedule.timeOfDay}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatNextRun(report.execution.nextRun)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={report.options.enabled ? report.status : 'disabled'}
                        color={getStatusColor(report.status, report.options.enabled)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {report.executionStats?.successRate?.toFixed(1) || 0}%
                        <Typography variant="caption" display="block">
                          ({report.execution.successCount}/{report.execution.runCount})
                        </Typography>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Run Now">
                          <IconButton
                            size="small"
                            onClick={() => handleRun(report._id)}
                            disabled={loading}
                          >
                            <RunIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(report)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Clone">
                          <IconButton
                            size="small"
                            onClick={() => handleClone(report)}
                          >
                            <CloneIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="View History">
                          <IconButton
                            size="small"
                            onClick={() => handleViewHistory(report._id)}
                          >
                            <HistoryIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title={report.options.enabled ? "Disable" : "Enable"}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(report._id, !report.options.enabled)}
                          >
                            <ScheduleIcon color={report.options.enabled ? "primary" : "disabled"} />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(report._id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {reports.length === 0 && (
            <Box textAlign="center" py={4}>
              <ReportIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No scheduled reports found
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Create your first scheduled report to automate your reporting workflow
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Create Report
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedReport ? 'Edit Scheduled Report' : 'Create Scheduled Report'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Basic Information</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Report Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={formData.reportType}
                  onChange={(e) => setFormData({ ...formData, reportType: e.target.value })}
                  required
                >
                  {templates.map((template) => (
                    <MenuItem key={template.type} value={template.type}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>

            {/* Schedule Configuration */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Schedule Configuration</Typography>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label="Time of Day"
                  value={new Date(`2000-01-01T${formData.timeOfDay}:00`)}
                  onChange={(time) => {
                    if (time) {
                      const timeString = time.toTimeString().substring(0, 5);
                      setFormData({ ...formData, timeOfDay: timeString });
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                >
                  <MenuItem value="America/New_York">Eastern Time</MenuItem>
                  <MenuItem value="America/Chicago">Central Time</MenuItem>
                  <MenuItem value="America/Denver">Mountain Time</MenuItem>
                  <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Report Parameters */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Report Parameters</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={formData.dateRangeType}
                  onChange={(e) => setFormData({ ...formData, dateRangeType: e.target.value })}
                >
                  <MenuItem value="last_day">Last Day</MenuItem>
                  <MenuItem value="last_week">Last Week</MenuItem>
                  <MenuItem value="last_month">Last Month</MenuItem>
                  <MenuItem value="last_quarter">Last Quarter</MenuItem>
                  <MenuItem value="last_year">Last Year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Format</InputLabel>
                <Select
                  value={formData.format}
                  onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                >
                  <MenuItem value="pdf">PDF</MenuItem>
                  <MenuItem value="excel">Excel</MenuItem>
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="html">HTML</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.includeCharts}
                    onChange={(e) => setFormData({ ...formData, includeCharts: e.target.checked })}
                  />
                }
                label="Include Charts"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.includeAllLocations}
                    onChange={(e) => setFormData({ ...formData, includeAllLocations: e.target.checked })}
                  />
                }
                label="Include All Locations"
              />
            </Grid>

            {/* Email Delivery */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Email Delivery</Typography>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.emailEnabled}
                    onChange={(e) => setFormData({ ...formData, emailEnabled: e.target.checked })}
                  />
                }
                label="Enable Email Delivery"
              />
            </Grid>
            
            {formData.emailEnabled && (
              <>
                {formData.emailRecipients.map((recipient, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={recipient.email}
                        onChange={(e) => updateEmailRecipient(index, 'email', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Name (Optional)"
                        value={recipient.name}
                        onChange={(e) => updateEmailRecipient(index, 'name', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => removeEmailRecipient(index)}
                        disabled={formData.emailRecipients.length === 1}
                        sx={{ height: '56px' }}
                      >
                        Remove
                      </Button>
                    </Grid>
                  </React.Fragment>
                ))}
                
                <Grid item xs={12}>
                  <Button variant="outlined" onClick={addEmailRecipient}>
                    Add Email Recipient
                  </Button>
                </Grid>
              </>
            )}

            {/* Options */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Options</Typography>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  />
                }
                label="Enable Report"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.name || !formData.reportType}>
            {selectedReport ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Report Execution History</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Execution Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Execution Time</TableCell>
                  <TableCell>Records</TableCell>
                  <TableCell>File Size</TableCell>
                  <TableCell>Delivery Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportHistory.map((execution: any, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {new Date(execution.executionDate).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={execution.status}
                        color={execution.status === 'success' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {execution.executionTime ? `${(execution.executionTime / 1000).toFixed(1)}s` : 'N/A'}
                    </TableCell>
                    <TableCell>{execution.recordsProcessed || 0}</TableCell>
                    <TableCell>
                      {execution.fileSize ? `${(execution.fileSize / 1024).toFixed(1)} KB` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {execution.deliveryStatus?.email?.sent ? 'Sent' : 'Failed'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduledReportsManagement;